import { useState, useRef, useEffect, useCallback } from "react";
import SignalingClient from "../store/features/meeting/signalingClient";

const ICE_CONFIG = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
  iceCandidatePoolSize: 10,
};

/**
 * useWebRTC — manages the entire WebRTC + Signaling lifecycle.
 *
 * @param {Object} options
 * @param {string} options.appointmentId
 * @param {string} options.token - Short-lived meeting token
 * @param {string} options.role - "CUSTOMER" | "COACH"
 * @param {Object} options.wsUrl - WebSocket URL
 * @param {Function} options.onMeetingEnded - Callback when meeting ends
 * @param {Function} options.onWarning - Callback when T-60s warning arrives
 * @param {Function} options.onParticipantJoined - Callback when other joins
 * @param {Function} options.onParticipantLeft - Callback when other leaves
 * @param {Function} options.onJoinError - Callback when join is rejected
 */
export default function useWebRTC({
  appointmentId,
  token,
  role,
  wsUrl,
  onMeetingEnded,
  onWarning,
  onParticipantJoined,
  onParticipantLeft,
  onJoinError,
}) {
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("DISCONNECTED");
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);

  const pcRef = useRef(null);
  const signalingRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const isCallerRef = useRef(false);
  const hasJoinedRef = useRef(false);
  const makingOffer = useRef(false);
  const ignoreOffer = useRef(false);
  const handleSignalingMessageRef = useRef(null);

  // Initialize
  useEffect(() => {
    if (!appointmentId || !token) {
      return undefined;
    }

    let mounted = true;

    const init = async () => {
      try {
        setConnectionStatus("CONNECTING");
        hasJoinedRef.current = false;

        // 1. Get user media
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        localStreamRef.current = stream;
        setLocalStream(stream);

        // 2. Create RTCPeerConnection
        const pc = new RTCPeerConnection(ICE_CONFIG);
        pcRef.current = pc;

        // Add local tracks
        stream.getTracks().forEach((track) => {
          pc.addTrack(track, stream);
        });

        // 3. Set up remote stream
        const remote = new MediaStream();
        remoteStreamRef.current = remote;
        setRemoteStream(remote);

        // 4. Handle remote tracks
        pc.ontrack = (event) => {
          event.streams[0].getTracks().forEach((track) => {
            remote.addTrack(track);
          });
          setRemoteStream(new MediaStream(remote.getTracks()));
        };

        // 5. Handle ICE connection state
        pc.oniceconnectionstatechange = () => {
          console.log("[useWebRTC] ICE state:", pc.iceConnectionState);
          if (
            pc.iceConnectionState === "disconnected" ||
            pc.iceConnectionState === "failed"
          ) {
            setConnectionStatus("DISCONNECTED");
          } else if (pc.iceConnectionState === "connected") {
            setConnectionStatus("CONNECTED");
          }
        };

        // 6. Handle connection state
        pc.onconnectionstatechange = () => {
          console.log("[useWebRTC] Connection state:", pc.connectionState);
          if (pc.connectionState === "connected") {
            setConnectionStatus("CONNECTED");
          } else if (
            pc.connectionState === "disconnected" ||
            pc.connectionState === "failed"
          ) {
            setConnectionStatus("DISCONNECTED");
          }
        };

        // 7. Handle negotiation needed
        pc.onnegotiationneeded = async () => {
          try {
            makingOffer.current = true;
            await pc.setLocalDescription(await pc.createOffer());
            signalingRef.current?.send("offer", {
              sdp: pc.localDescription,
            });
          } catch (err) {
            console.error("[useWebRTC] Negotiation error:", err);
          } finally {
            makingOffer.current = false;
          }
        };

        // 8. Handle ICE candidates
        pc.onicecandidate = (event) => {
          if (event.candidate) {
            signalingRef.current?.send("ice-candidate", {
              candidate: event.candidate.toJSON(),
            });
          }
        };

        // 9. Connect signaling
        // Use REACT_APP_WS_URI if set (e.g. wss://ngrok-url/ws),
        // otherwise derive from REACT_APP_API_URI
        // e.g. http://localhost:8000/api -> ws://localhost:8000/ws
        // e.g. https://ngrok-url/api -> wss://ngrok-url/ws
        const wsBaseUrl =
          wsUrl ||
          process.env.REACT_APP_WS_URI ||
          (() => {
            const apiUrl = process.env.REACT_APP_API_URI || "";
            const protocol = apiUrl.startsWith("https") ? "wss:" : "ws:";
            // Strip /api suffix and protocol prefix to get host:port
            const host = apiUrl
              .replace(/^https?:\/\//, "")
              .replace(/\/api\/?$/, "");
            return `${protocol}//${host}/ws`;
          })();
        const signalingUrl = `${wsBaseUrl}?appointmentId=${appointmentId}&token=${token}`;

        const client = new SignalingClient({
          url: signalingUrl,
          onOpen: () => {
            console.log("[useWebRTC] Signaling connected, joining meeting");
            client.send("join-meeting", { appointmentId, token });
          },
          onMessage: (message) => {
            handleSignalingMessageRef.current?.(message, pc, client, remote);
          },
          onClose: () => {
            console.log("[useWebRTC] Signaling closed");
          },
          onError: (err) => {
            console.error("[useWebRTC] Signaling error:", err);
          },
        });

        signalingRef.current = client;
        client.connect();
      } catch (err) {
        console.error("[useWebRTC] Initialization error:", err);
        setConnectionStatus("ERROR");
      }
    };

    init();

    return () => {
      mounted = false;
      cleanup();
    };
    // handleSignalingMessage is read via ref so callback identity changes
    // do not tear down the peer connection.
  }, [appointmentId, token, wsUrl]);

  // --- Signaling message handler ---
  const handleSignalingMessage = useCallback(
    async (message, pc, client, remoteStreamObj) => {
      const { type, payload } = message;

      switch (type) {
        case "join-ack": {
          const { otherPresent, role: joinedRole } = payload;
          hasJoinedRef.current = true;
          isCallerRef.current = otherPresent; // If other is already there, we are the callee
          if (!otherPresent) {
            setConnectionStatus("WAITING");
          }
          if (joinedRole) {
            // role already known
          }
          break;
        }

        case "reconnect-ack": {
          hasJoinedRef.current = true;
          const { otherPresent: reconnectOtherPresent } = payload;
          if (reconnectOtherPresent) {
            // Re-negotiate
            try {
              await pc.setLocalDescription(await pc.createOffer());
              client.send("offer", { sdp: pc.localDescription });
            } catch (err) {
              console.error("[useWebRTC] Reconnect renegotiation error:", err);
            }
          } else {
            setConnectionStatus("WAITING");
          }
          break;
        }

        case "participant-joined": {
          setConnectionStatus("NEGOTIATING");
          if (onParticipantJoined) onParticipantJoined(payload);
          break;
        }

        case "participant-left": {
          setConnectionStatus("WAITING");
          if (onParticipantLeft) onParticipantLeft(payload);
          break;
        }

        case "negotiation-ready": {
          // Both present, start negotiation if we're the caller
          if (!isCallerRef.current) {
            try {
              await pc.setLocalDescription(await pc.createOffer());
              client.send("offer", { sdp: pc.localDescription });
            } catch (err) {
              console.error("[useWebRTC] Negotiation-ready error:", err);
            }
          }
          break;
        }

        case "offer": {
          const isWrongCollation =
            pc.signalingState !== "stable" && !makingOffer.current;
          ignoreOffer.current = !isCallerRef.current && isWrongCollation;
          if (ignoreOffer.current) break;

          try {
            await pc.setRemoteDescription(
              new RTCSessionDescription(payload.sdp),
            );
            await pc.setLocalDescription(await pc.createAnswer());
            client.send("answer", { sdp: pc.localDescription });
          } catch (err) {
            console.error("[useWebRTC] Error handling offer:", err);
          }
          break;
        }

        case "answer": {
          if (pc.signalingState === "have-local-offer") {
            try {
              await pc.setRemoteDescription(
                new RTCSessionDescription(payload.sdp),
              );
            } catch (err) {
              console.error("[useWebRTC] Error handling answer:", err);
            }
          }
          break;
        }

        case "ice-candidate": {
          if (payload.candidate && !ignoreOffer.current) {
            try {
              await pc.addIceCandidate(new RTCIceCandidate(payload.candidate));
            } catch (err) {
              console.error("[useWebRTC] Error adding ICE candidate:", err);
            }
          }
          break;
        }

        case "meeting-warning": {
          if (onWarning) onWarning(payload);
          break;
        }

        case "meeting-ended": {
          if (onMeetingEnded) onMeetingEnded(payload);
          break;
        }

        case "error": {
          const errorCode = payload?.code;
          const errorMessage = payload?.message || "Failed to join the meeting";
          const isJoinFailure =
            !hasJoinedRef.current ||
            errorCode === "SESSION_ALREADY_ACTIVE" ||
            errorCode === "AUTH_FAILED" ||
            errorCode === "FORBIDDEN" ||
            errorCode === "JOIN_FAILED";

          if (isJoinFailure) {
            hasJoinedRef.current = false;

            if (localStreamRef.current) {
              localStreamRef.current.getTracks().forEach((t) => t.stop());
              localStreamRef.current = null;
            }

            if (pcRef.current) {
              pcRef.current.close();
              pcRef.current = null;
            }

            if (client) {
              client.close();
            }
            signalingRef.current = null;

            setLocalStream(null);
            setRemoteStream(null);
            setConnectionStatus("ERROR");

            if (onJoinError) {
              onJoinError(errorMessage);
            }
            break;
          }

          console.error("[useWebRTC] Server error:", payload);
          break;
        }

        default:
          break;
      }
    },
    [
      onMeetingEnded,
      onWarning,
      onParticipantJoined,
      onParticipantLeft,
      onJoinError,
    ],
  );
  handleSignalingMessageRef.current = handleSignalingMessage;

  // --- Toggle mic ---
  const toggleMic = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMicMuted(!audioTrack.enabled);
      }
    }
  }, []);

  // --- Toggle camera ---
  const toggleCamera = useCallback(() => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsCameraOff(!videoTrack.enabled);
      }
    }
  }, []);

  // --- End call ---
  const endCall = useCallback(() => {
    if (signalingRef.current) {
      signalingRef.current.send("participant-left", {
        reason: "user_initiated",
      });
    }
    cleanup();
  }, []);

  // --- Cleanup ---
  const cleanup = () => {
    hasJoinedRef.current = false;

    // Stop local media tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }

    // Close peer connection
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }

    // Close signaling
    if (signalingRef.current) {
      signalingRef.current.close();
      signalingRef.current = null;
    }

    setLocalStream(null);
    setRemoteStream(null);
    setConnectionStatus("DISCONNECTED");
  };

  return {
    localStream,
    remoteStream,
    connectionStatus,
    isMicMuted,
    isCameraOff,
    toggleMic,
    toggleCamera,
    endCall,
    cleanup,
  };
}
